import React from 'react';
import PropTypes from 'prop-types';
import defaults from 'lodash/defaults';
import Measure from 'react-measure';

import videojs from 'video.js';
import qualityLevels from 'videojs-contrib-quality-levels';

import Observer from '@researchgate/react-intersection-observer';

import { VideojsGlobalStyles } from './video-js.min';
import { VideojsIconsGlobalStyles } from './videojs-icons';

import skin from './skin.css';

import EventHandler from './EventHandler';
import PlayerApi from './PlayerApi';
import { getPoster } from './utils';

import {
  QualityLevels,
  Subtitles,
  setupThumbnails,
  addElementToVideojs,
  Menu,
  AutoplaySettings,
} from './controls';
import {
  getDefaultVolume,
  getDefaultMutedState,
  getAllowAutoplayState,
} from './settings';

// _lazy_ load intersection observer polyfill
// but only if we are on browser, because
// the polyfill wants to populate the window object
if (typeof window !== 'undefined') {
  require('intersection-observer');
}

class Player extends React.Component {
  state = {
    layout: 'layout-normal',
  };

  xingPlayer = {};

  componentDidMount() {
    const configuration = defaults(this.props.configuration, {
      autostart: false,
      preload: true,
      mute: getDefaultMutedState(),
    });

    const videoJsOptions = {
      aspectRatio: this.props.isStory ? '9:16' : '16:9',
      controls: configuration.controls || true,
      resizeManager: false,
      playsinline: true,
      /* sources: [{
        src: 'https://bitdash-a.akamaihd.net/content/MI201109210084_1/m3u8s/f08e80da-bf1d-4e3d-8899-f0f6155f6efa.m3u8',
        type: 'application/x-mpegURL',
      }], */
      sources: this.props.sources,
      poster: getPoster(
        this.props.posterImage,
        this.props.thumbnails,
        this.videoNode?.parentElement?.clientWidth
      ),
      autoplay:
        this.props.forceAutoplay ||
        (configuration.autostart && getAllowAutoplayState()),
      preload: configuration.preload,
      muted: this.props.forceAutoplay || configuration.mute,
      html5: {
        vhs: {
          overrideNative:
            window.navigator.userAgent.toLowerCase().indexOf('android') >= 0,
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
      disablePictureInPicture: configuration.disablePictureInPicture || false,
    };

    this.xingPlayer = {
      videojs: videojs(this.videoNode, videoJsOptions, () =>
        this.playerReadyHandler()
      ),
      videoId: this.props.id,
    };

    this.xingPlayer.videojs.qualityLevels = qualityLevels;

    this.xingPlayer.videojs.addClass(skin.vjsxing);
    this.xingPlayer.videojs.addClass(skin.videojs);

    this.playerApi = PlayerApi(this.xingPlayer, getAllowAutoplayState);

    // set default volume (based on user preferences)
    this.xingPlayer.videojs.volume(getDefaultVolume());
  }

  // destroy player on unmount
  componentWillUnmount() {
    if (this.xingPlayer.videojs) {
      this.xingPlayer.videojs.dispose();
    }
  }

  playerReadyHandler() {
    // initialize video js api
    this.props.actions && this.props.actions(this.playerApi);

    // init menu
    addElementToVideojs(
      this.xingPlayer.videojs,
      this.xingPlayer.videojs,
      Menu,
      {
        callback: (setMenu) => {
          this.xingPlayer.setMenu = setMenu;
        },
      }
    );

    // add quality levels
    addElementToVideojs(
      this.xingPlayer.videojs.getChild('controlBar'),
      this.xingPlayer.videojs,
      QualityLevels,
      { xingPlayer: this.xingPlayer },
      8
    );

    // add subtitles
    this.props.subtitles &&
      addElementToVideojs(
        this.xingPlayer.videojs.getChild('controlBar'),
        this.xingPlayer.videojs,
        Subtitles,
        {
          xingPlayer: this.xingPlayer,
          subtitles: this.props.subtitles,
          shouldDisplaySubtitle: this.xingPlayer.videojs.muted(),
        },
        9
      );

    // add thumbnails
    this.props.sprites && setupThumbnails(this.xingPlayer, this.props.sprites);

    // initialize events/tracking
    this.intializeEventHandler();
  }

  intializeEventHandler() {
    this.eventHandler = new EventHandler(
      this.xingPlayer,
      this.props.section,
      this.props.trackingExtraFields,
      this.props,
      this.props.trackEventMutation
    );
  }

  handleIntersection(intersectionRatio) {
    const inView = intersectionRatio > 0.5;

    if (this.xingPlayer.inView !== inView) {
      this.xingPlayer.inView = inView;
      this.eventHandler &&
        this.eventHandler.handleIntersection &&
        this.eventHandler.handleIntersection(inView);
    }
  }

  setBreakpoint({ bounds }) {
    this.setState({
      layout:
        bounds.width < 350
          ? 'layout-tiny'
          : bounds.width < 500
          ? 'layout-small'
          : 'layout-normal',
    });
  }

  // wrap the player in a div with a `data-vjs-player` attribute
  // so videojs won't create additional wrapper in the DOM
  // see https://github.com/videojs/video.js/pull/3856
  render() {
    return (
      <Measure bounds onResize={(cr) => this.setBreakpoint(cr)}>
        {({ measureRef }) => (
          <div
            className={`${this.state.layout} ${
              this.props.isStory ? skin.videoStory : ''
            }`}
          >
            <div data-vjs-player ref={measureRef}>
              <AutoplaySettings />
              <VideojsGlobalStyles />
              <VideojsIconsGlobalStyles />
              <Observer
                threshold={[0, 0.5]}
                onChange={({ intersectionRatio }) =>
                  this.handleIntersection(intersectionRatio)
                }
              >
                <video
                  ref={(node) => (this.videoNode = node)}
                  className="video-js"
                />
              </Observer>
            </div>
          </div>
        )}
      </Measure>
    );
  }
}

Player.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  section: PropTypes.string.isRequired,
  configuration: PropTypes.object.isRequired,
  actions: PropTypes.func,
  onReady: PropTypes.func,
  onStart: PropTypes.func,
  onMute: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
  sources: PropTypes.arrayOf(PropTypes.string).isRequired,
  thumbnails: PropTypes.array,
  posterImage: PropTypes.string,
  sprites: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
    })
  ),
  subtitles: PropTypes.any,
  trackEventMutation: PropTypes.func,
  forceAutoplay: PropTypes.bool,
  trackingExtraFields: PropTypes.object,
  isStory: PropTypes.bool,
};

export default Player;
